import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, STATUSES, STATUS_META, MONTHS_IT, DOW_IT, pad, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CaretLeft, CaretRight, MagnifyingGlass, PaintBrush, FilePdf } from "@phosphor-icons/react";
import EmployeeSummary from "@/components/EmployeeSummary";

const YEARS = Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 4 + i);

export default function AttendanceCalendar() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const target = params.get("data");
  const canEdit = ["admin", "manager"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const now = target ? new Date(target) : new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState({});
  const [days, setDays] = useState(31);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [empStatus, setEmpStatus] = useState("active");
  const [brush, setBrush] = useState("");
  const [cell, setCell] = useState(null);
  const [summaryId, setSummaryId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [emps, att] = await Promise.all([
        api.get("/employees", { params: { q, department, status: empStatus } }),
        api.get("/attendance", { params: { year, month } }),
      ]);
      setEmployees(emps.data);
      setDays(att.data.days);
      const map = {};
      att.data.records.forEach((r) => { map[`${r.employee_id}|${r.date}`] = r; });
      setRecords(map);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [year, month, q, department, empStatus]);

  useEffect(() => { load(); }, [load]);

  const dayList = useMemo(() => Array.from({ length: days }, (_, i) => i + 1), [days]);
  const dateOf = (d) => `${year}-${pad(month)}-${pad(d)}`;
  const isWeekend = (d) => [0, 6].includes(new Date(year, month - 1, d).getDay());

  const save = async (payload, silent) => {
    try {
      const { data } = await api.put("/attendance", { ...payload, entry_time: null, exit_time: null, break_minutes: 0 });
      setRecords((prev) => ({ ...prev, [`${data.employee_id}|${data.date}`]: data }));
      if (!silent) toast.success("Presenza salvata");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  const onCellClick = (emp, d) => {
    if (!canEdit) return;   // il Titolare consulta il calendario in sola lettura
    const date = dateOf(d);
    const ex = records[`${emp.id}|${date}`];
    if (brush) {
      save({ employee_id: emp.id, date, status: brush, notes: ex?.notes || null }, true);
    } else {
      setCell({ emp, date, rec: ex });
    }
  };

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <div data-testid="calendar-page" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Calendario Presenze</h1>
          <p className="text-sm text-muted-foreground mt-1 tabular-nums">
            {MONTHS_IT[month - 1]} {year} · {employees.length} dipendenti
            {!canEdit && " · sola lettura"}
          </p>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm" onClick={() => shift(-1)} data-testid="prev-month">
            <CaretLeft size={15} />
          </Button>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="h-9 w-[130px] rounded-sm" data-testid="month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-9 w-[92px] rounded-sm" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm" onClick={() => shift(1)} data-testid="next-month">
            <CaretRight size={15} />
          </Button>
          <Button variant="secondary" size="sm" className="h-9 rounded-sm font-semibold" data-testid="today-btn"
            onClick={() => { setMonth(new Date().getMonth() + 1); setYear(new Date().getFullYear()); }}>
            Oggi
          </Button>
        </div>

        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="calendar-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca nome, cognome, codice..." className="h-9 w-56 pl-8 rounded-sm" />
        </div>

        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-9 w-[160px] rounded-sm" data-testid="calendar-department"><SelectValue placeholder="Reparto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i reparti</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={empStatus} onValueChange={setEmpStatus}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm" data-testid="calendar-emp-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Attivi</SelectItem>
            <SelectItem value="inactive">Non attivi</SelectItem>
            <SelectItem value="all">Tutti</SelectItem>
          </SelectContent>
        </Select>

        <Button size="sm" className="h-9 rounded-sm font-semibold" data-testid="pdf-monthly"
          onClick={() => download(
            `/export/monthly-pdf?year=${year}&month=${month}&department=${department}&status=${empStatus}`,
            `report_mensile_${year}_${pad(month)}.pdf`,
          ).catch((e) => toast.error(errMsg(e)))}>
          <FilePdf size={15} className="mr-1.5" /> PDF Mensile
        </Button>

        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {canEdit && (
            <>
              <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <PaintBrush size={14} /> Rapido
              </span>
              {STATUSES.map((s) => (
                <button key={s} data-testid={`brush-${s}`} onClick={() => setBrush(brush === s ? "" : s)}
                  className={`h-8 px-2 rounded-sm text-[11px] font-bold border transition-colors duration-150
                    ${brush === s ? "border-primary ring-2 ring-primary/40" : "border-border"} ${STATUS_META[s].cell}`}>
                  {STATUS_META[s].short}
                </button>
              ))}
              {brush && (
                <button onClick={() => setBrush("")} data-testid="brush-clear"
                  className="h-8 px-2 rounded-sm text-[11px] font-semibold border border-border hover:bg-secondary">
                  Annulla
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-auto scroll-thin" style={{ maxHeight: "70vh" }}>
        <table className="border-collapse text-sm tbl-sticky" data-testid="attendance-grid">
          <thead className="sticky top-0 z-10">
            <tr className="bg-secondary">
              <th className="sticky-col bg-secondary border-b border-r border-border px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground min-w-[140px] sm:min-w-[210px]">
                Dipendente
              </th>
              {dayList.map((d) => (
                <th key={d} className={`border-b border-r border-border w-[30px] px-0 py-1 text-center text-[11px] font-semibold tabular-nums
                  ${isWeekend(d) ? "bg-muted text-muted-foreground" : ""}`}>
                  <div>{d}</div>
                  <div className="text-[9px] font-normal opacity-60">{DOW_IT[new Date(year, month - 1, d).getDay()]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={days + 1} className="px-3 py-8 text-center text-sm text-muted-foreground">Caricamento...</td></tr>
            )}
            {!loading && employees.length === 0 && (
              <tr><td colSpan={days + 1} className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="grid-empty">Nessun dipendente trovato</td></tr>
            )}
            {!loading && employees.map((emp) => (
              <tr key={emp.id} className="hover:bg-secondary/40">
                <td className="sticky-col border-b border-r border-border px-2 py-1">
                  <button data-testid={`employee-name-${emp.code}`} onClick={() => setSummaryId(emp.id)}
                    className="text-left hover:text-primary transition-colors duration-150">
                    <div className="text-[13px] font-semibold leading-tight">{emp.last_name} {emp.first_name}</div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">{emp.code} · {emp.department}{!emp.active && " · NON ATTIVO"}</div>
                  </button>
                </td>
                {dayList.map((d) => {
                  const rec = records[`${emp.id}|${dateOf(d)}`];
                  const meta = rec ? STATUS_META[rec.status] : null;
                  return (
                    <td key={d} className={`border-b border-r border-border p-0 ${isWeekend(d) ? "bg-muted/40" : ""}`}>
                      <button
                        data-testid={`cell-${emp.code}-${d}`}
                        title={rec ? `${rec.status}${rec.notes ? ` · ${rec.notes}` : ""}` : "Non registrato"}
                        onClick={() => onCellClick(emp, d)}
                        className={`grid-cell h-8 w-[30px] text-[10px] font-bold focus:outline-none focus:ring-2 focus:ring-primary
                          ${meta ? meta.cell : canEdit ? "hover:bg-accent" : ""} ${canEdit ? "" : "cursor-default"}`}>
                        {meta ? meta.short : ""}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px]">
        {STATUSES.map((s) => (
          <span key={s} className="flex items-center gap-1.5 font-medium text-muted-foreground">
            <span className={`h-3 w-5 rounded-sm border border-border ${STATUS_META[s].cell}`} /> {s} ({STATUS_META[s].short})
          </span>
        ))}
      </div>

      {cell && <CellDialog cell={cell} onClose={() => setCell(null)} onSave={save} isAdmin={isAdmin}
        onDelete={async () => {
          try {
            await api.delete("/attendance", { params: { employee_id: cell.emp.id, date: cell.date } });
            setRecords((p) => { const n = { ...p }; delete n[`${cell.emp.id}|${cell.date}`]; return n; });
            toast.success("Registrazione eliminata");
            setCell(null);
          } catch (e) { toast.error(errMsg(e)); }
        }} />}

      {summaryId && <EmployeeSummary employeeId={summaryId} year={year} month={month} onClose={() => setSummaryId(null)} />}
    </div>
  );
}

function CellDialog({ cell, onClose, onSave, isAdmin, onDelete }) {
  const r = cell.rec || {};
  const [status, setStatus] = useState(r.status || "PRESENTE");
  const [notes, setNotes] = useState(r.notes || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const ok = await onSave({ employee_id: cell.emp.id, date: cell.date, status, notes: notes || null });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="cell-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">
            {cell.emp.last_name} {cell.emp.first_name}
            <div className="text-xs font-normal text-muted-foreground tabular-nums mt-0.5">
              {new Date(cell.date).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Causale della giornata</Label>
          <div className="grid grid-cols-3 gap-1.5 mt-1.5">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`dialog-status-${s}`} onClick={() => setStatus(s)}
                className={`h-9 rounded-sm text-[11px] font-bold border transition-colors duration-150
                  ${status === s ? "border-primary ring-2 ring-primary/40" : "border-border"} ${STATUS_META[s].cell}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-9 rounded-sm mt-1" data-testid="dialog-notes" placeholder="Opzionale" />
        </div>

        <DialogFooter className="gap-2">
          {isAdmin && cell.rec && (
            <Button variant="outline" className="rounded-sm text-destructive" onClick={onDelete} data-testid="dialog-delete">Elimina</Button>
          )}
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="dialog-cancel">Annulla</Button>
          <Button className="rounded-sm font-semibold" onClick={submit} disabled={busy} data-testid="dialog-save">
            {busy ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
